// A list of model changes grouped by entity, as shown for timeline steps, sketches and comparisons.
import { groupChanges, type ModelChange, type ModelSpec } from '@modelviewer/core';
import { Minus, Plus } from 'lucide-react';
import type { DiffState } from '../diagram/graph';
import { cn } from '../lib/cn';

export function StateIcon({ state }: { state: DiffState }) {
  const cls = 'flex h-4 w-4 flex-none items-center justify-center rounded';
  if (state === 'added')
    return (
      <span className={cn(cls, 'bg-added-soft text-added')}>
        <Plus size={11} strokeWidth={2.5} aria-label="Added" />
      </span>
    );
  if (state === 'removed')
    return (
      <span className={cn(cls, 'bg-removed-soft text-removed')}>
        <Minus size={11} strokeWidth={2.5} aria-label="Removed" />
      </span>
    );
  return (
    <span className={cn(cls, 'bg-changed-soft text-changed')}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-label="Changed" />
    </span>
  );
}

const TEXT_COLOR: Record<DiffState, string> = { added: 'text-added', removed: 'text-removed', changed: 'text-changed' };

export function ChangeList(props: {
  changes: ModelChange[];
  after: ModelSpec;
  before: ModelSpec;
  /** An entity was clicked: `removed` when it exists only in the older model. */
  onReveal: (key: string, removed: boolean) => void;
  empty: React.ReactNode;
}) {
  const groups = groupChanges(props.changes, props.after, props.before);
  if (groups.length === 0) return <p className="px-3 py-3 text-[12.5px] text-muted">{props.empty}</p>;
  return (
    <ul className="px-1.5 py-1.5" data-testid="model-changes">
      {groups.map((g) => (
        <li key={`${g.state}:${g.key}`} className="mb-1">
          <button
            className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-hover disabled:hover:bg-transparent"
            disabled={g.key === ''}
            onClick={() => props.onReveal(g.key, g.state === 'removed')}
          >
            <StateIcon state={g.state} />
            <span className={cn('min-w-0 truncate text-[12.5px] font-semibold', g.state === 'removed' && 'line-through decoration-removed')}>{g.key || 'Model'}</span>
            {g.note && <span className="flex-none text-[11.5px] text-changed">{g.note}</span>}
          </button>
          {g.items.length > 0 && (
            <ul className="ml-[30px] border-l border-border pl-2">
              {g.items.map((item, i) => (
                <li key={i} className="py-[3px] text-[12px] leading-snug">
                  <span className={cn('mr-1.5 inline-block w-2 font-mono', TEXT_COLOR[item.state])} aria-hidden>
                    {item.state === 'added' ? '+' : item.state === 'removed' ? '−' : '~'}
                  </span>
                  <span className="text-text">{item.text}</span>
                  {item.detail && <span className="ml-1.5 font-mono text-[11px] text-muted">{item.detail}</span>}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}
